<div class="ai-insights">
   <!-- ── Header row ─────────────────────────────────────────────────────── -->
   <div class="ai-header-row">
      <div>
         <h1 class="ai-title">AI Insights - Vetcor Performance Reviews</h1>
         <div class="ai-sub">
            AI reads every written answer submitted for the selected review type and summarizes what people are consistently saying — the good and the
            bad — in plain language. Sections are ordered exactly as they appear in the review.
         </div>
      </div>
      <div class="ai-controls">
         <div class="ai-select-wrap">
            <span
               class="ai-type-dot"
               [style.background]="selectedOption.color"></span>
            <select
               class="ai-select"
               [(ngModel)]="selectedKey"
               (change)="onReviewTypeChange()"
               [disabled]="generating">
               <option
                  *ngFor="let opt of reviewTypeOptions"
                  [value]="opt.key">
                  {{ opt.label }}
               </option>
            </select>
         </div>
         <button
            class="ai-btn-ghost"
            (click)="exportPdf()"
            [disabled]="!result">
            <i class="fa fa-download"></i>
            &nbsp; Export PDF
         </button>
         <button
            class="ai-btn-primary"
            (click)="generate()"
            [disabled]="generating || loading">
            <ng-container *ngIf="!generating">✦&nbsp; {{ record ? "Re-run Analysis" : "Generate Analysis" }}</ng-container>
            <ng-container *ngIf="generating">
               <i class="fa fa-spinner fa-spin"></i>
               &nbsp; Analyzing…
            </ng-container>
         </button>
      </div>
   </div>

   <!-- ── Error ──────────────────────────────────────────────────────────── -->
   <div
      class="ai-error"
      *ngIf="error">
      {{ error }}
   </div>

   <!-- ── Loading existing insights ──────────────────────────────────────── -->
   <div
      class="ai-loading"
      *ngIf="loading">
      <i class="fa fa-spinner fa-spin"></i>
      &nbsp; Loading insights…
   </div>

   <!-- ── Generating state ───────────────────────────────────────────────── -->
   <div
      class="ai-generating"
      *ngIf="generating">
      <div class="ai-generating-spark">✦</div>
      <div>
         <div class="ai-generating-title">Analyzing written answers for {{ selectedOption.label }}…</div>
         <div class="ai-generating-sub">
            The AI is reading every comment and grouping them into themes. This usually takes about a minute — please keep this tab open.
         </div>
      </div>
   </div>

   <!-- ── Empty state ────────────────────────────────────────────────────── -->
   <div
      class="ai-empty"
      *ngIf="!loading && !generating && !record && !error">
      <div class="ai-empty-icon">✦</div>
      <h3>No analysis yet for {{ selectedOption.label }}</h3>
      <p>
         Click
         <b>Generate Analysis</b>
         to have the AI read all written answers submitted for this review type and summarize the recurring positive and negative themes.
      </p>
      <button
         class="ai-btn-primary"
         (click)="generate()">
         ✦&nbsp; Generate Analysis
      </button>
   </div>

   <!-- ── Results ────────────────────────────────────────────────────────── -->
   <ng-container *ngIf="!loading && !generating && result">
      <!-- Executive summary banner -->
      <div class="ai-banner">
         <div class="ai-banner-spark">✦</div>
         <div>
            <h2>{{ selectedOption.label | uppercase }} REVIEWS — AI SUMMARY</h2>
            <p>{{ result.executiveSummary }}</p>
            <div class="ai-banner-meta">
               Based on written answers from {{ record?.reviewCount | number }} completed reviews ({{ record?.commentCount | number }} written
               answers) · Last generated {{ record?.generatedDate | date: "MMM d, y h:mm a" }}
               <ng-container *ngIf="record?.generatedByName">by {{ record?.generatedByName }}</ng-container>
            </div>
         </div>
      </div>

      <!-- Stat cards -->
      <div class="ai-statgrid">
         <div
            class="ai-stat"
            [style.border-top-color]="'#ffc678'">
            <div class="ai-stat-label">Reviews Analyzed</div>
            <div class="ai-stat-num">{{ record?.reviewCount | number }}</div>
            <div class="ai-stat-hint">{{ selectedOption.label }} reviews with written answers</div>
         </div>
         <div class="ai-stat ai-stat-pos">
            <div class="ai-stat-label">Things Going Well</div>
            <div class="ai-stat-num">{{ positiveCount }}</div>
            <div class="ai-stat-hint">sections with mostly positive answers</div>
         </div>
         <div class="ai-stat ai-stat-neg">
            <div class="ai-stat-label">Things Needing Attention</div>
            <div class="ai-stat-num">{{ attentionCount }}</div>
            <div class="ai-stat-hint">sections with mostly concerns</div>
         </div>
         <div
            class="ai-stat"
            [class.ai-stat-pos]="result.overallTone == 'Positive'"
            [class.ai-stat-neg]="result.overallTone == 'Negative'">
            <div class="ai-stat-label">Overall Tone</div>
            <div class="ai-stat-num">{{ result.overallTone }}</div>
            <div class="ai-stat-hint">across all written answers</div>
         </div>
      </div>

      <!-- Topic map: one tile per section -->
      <div class="ai-map-card">
         <div class="ai-map-head">
            <div>
               <h3>Topic Map — {{ selectedOption.label }} Review</h3>
               <div class="ai-map-hint">
                  One tile per section of the review. The color shows the overall tone of written answers. Click a tile to jump to that section below.
               </div>
            </div>
            <div class="ai-legend">
               <span>
                  <i class="tone-pos-strong"></i>
                  Mostly positive
               </span>
               <span>
                  <i class="tone-pos-soft"></i>
                  Leaning positive
               </span>
               <span>
                  <i class="tone-mixed"></i>
                  Mixed
               </span>
               <span>
                  <i class="tone-neg-soft"></i>
                  Leaning negative
               </span>
               <span>
                  <i class="tone-neg-strong"></i>
                  Mostly concerns
               </span>
            </div>
         </div>

         <div *ngIf="selectedOption.label == 'Team Member'">
            <div
               class="ai-section-group"
               *ngFor="let group of groupedSections">
               <h3 class="ai-section-header">
                  <i
                     class="fa fa-user"
                     style="color: #c5c5c5; margin-left: 8px"></i>
                  &nbsp;{{ group.type }}
               </h3>
               <div class="ai-tiles">
                  <div
                     class="ai-tile"
                     *ngFor="let s of group.sections; trackBy: trackBySectionId"
                     [ngClass]="toneClass(s.tone)"
                     (click)="jumpToSection(s.sectionId)">
                     <b>{{ s.sectionTitle }}</b>
                     <small>{{ s.oneLine }}</small>
                  </div>
               </div>
            </div>
         </div>

         <div
            class="ai-tiles"
            *ngIf="selectedOption.label != 'Team Member'">
            <div
               class="ai-tile"
               *ngFor="let s of result.sections; trackBy: trackBySectionId"
               [ngClass]="toneClass(s.tone)"
               (click)="jumpToSection(s.sectionId)">
               <b>{{ s.sectionTitle }}</b>
               <small>{{ s.oneLine }}</small>
            </div>
         </div>
      </div>

      <!-- Section-by-section breakdown -->
      <div class="ai-list-head">
         <div>
            <h3>Section-by-Section Breakdown</h3>
            <div class="ai-map-hint">
               These are the actual sections of the {{ selectedOption.label }} review, in order. The colored bar shows the overall tone of written
               answers in that section.
            </div>
         </div>
         <span
            class="ai-expand-all"
            (click)="expandAll()">
            Expand all sections
         </span>
      </div>

      <div
         class="ai-section"
         *ngFor="let s of result.sections; trackBy: trackBySectionId"
         [id]="'ai-section-' + s.sectionId">
         <div
            class="ai-section-summary"
            (click)="toggleSection(s.sectionId)">
            <span
               class="ai-tone-bar"
               [ngClass]="toneClass(s.tone)"></span>
            <div class="ai-section-titles">
               <h4>{{ s.sectionTitle }}</h4>
               <div class="ai-oneline">{{ s.oneLine }}</div>
            </div>
            <span
               class="ai-tonechip"
               [ngClass]="'chip-' + toneClass(s.tone)">
               {{ toneLabel(s.tone) }}
            </span>
            <span
               class="ai-chev"
               [class.open]="isExpanded(s.sectionId)">
               ›
            </span>
         </div>

         <div
            class="ai-section-inner"
            *ngIf="isExpanded(s.sectionId)">
            <p class="ai-section-aisum">{{ s.summary }}</p>

            <div
               class="ai-split"
               *ngIf="s.working.length || s.struggles.length">
               <div *ngIf="s.struggles.length">
                  <h5 class="ai-neg-h">📌 What people said is a struggle</h5>
                  <div
                     class="ai-theme-item"
                     *ngFor="let t of s.struggles">
                     <span class="ai-freq">{{ t.frequency }}</span>
                     <b>{{ t.title }}</b>
                     <p>{{ t.detail }}</p>
                     <div
                        class="ai-quote"
                        *ngIf="t.quote">
                        "{{ t.quote }}"
                        <span
                           class="ai-quote-src"
                           *ngIf="t.quoteSource">
                           {{ t.quoteSource }}
                        </span>
                     </div>
                  </div>
               </div>
               <div *ngIf="s.working.length">
                  <h5 class="ai-pos-h">✓ What people said is working</h5>
                  <div
                     class="ai-theme-item"
                     *ngFor="let t of s.working">
                     <span class="ai-freq">{{ t.frequency }}</span>
                     <b>{{ t.title }}</b>
                     <p>{{ t.detail }}</p>
                     <div
                        class="ai-quote"
                        *ngIf="t.quote">
                        "{{ t.quote }}"
                        <span
                           class="ai-quote-src"
                           *ngIf="t.quoteSource">
                           {{ t.quoteSource }}
                        </span>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <div class="ai-footnote">
         ⚠ This analysis was generated by AI from written review answers. Quotes are lightly anonymized. Sections with very few written answers may be
         omitted. Always verify findings before making decisions.
      </div>
   </ng-container>
</div>
